import { useCallback, useMemo } from "react";
import { useGlobalContext } from "./GlobalState";
import { changePassword, login, LoginResponse, logout, signup } from "./SessionApi";
import { SignupClient } from "../Api/client/CompetitionClient";

export enum Role {
   	DEVELOPER = "DEVELOPER",
	ADMIN = "ADMIN",
    OFFICIAL = "OFFICIAL",
	LEADER = "LEADER",
	MEMBER = "MEMBER",
	USER = "USER"
}





/**
 *  Returns the role with the highest "clearence" in roleStr or null is no role exist
 */
export function getHighestRoleInString(roleStr: string | null) : Role | null {
    const hasRole = (role: Role) => stringContainsRole(roleStr, role);
    return hasRole(Role.DEVELOPER) || hasRole(Role.ADMIN) || hasRole(Role.OFFICIAL) || hasRole(Role.LEADER) || hasRole(Role.MEMBER) || hasRole(Role.USER);
}

/**
 *  @returns the role if existing in roleStr else returns null
 */
export function stringContainsRole(roleStr: string | null, role: Role) : Role | null {
    return roleStr?.includes(role) ? role : null;
}


/**
 * Hook with authorization information updated with the global context.
 * 
 * @returns Array where
 * Entry 1 = The "highest" role the user is authorized as.
 * Entry 2 = A function taking role as input and returns true if user has these rights else false.
 */
export function useSessionState() {
    const globalState = useGlobalContext();

    const role = useMemo(() => {
        return getHighestRoleInString(globalState?.roles)
    }, [globalState?.roles])

    const hasAtLeastRole = useCallback((role: Role) => {
        return stringContainsRole(globalState?.roles, role) != null;
    }, [globalState?.roles])

    const loginAndUpdateGlobalState = useCallback(async (username?: string, password?: string) => {
        return login(username, password).then((response) => {
            console.log("Login info: ", response.user, response.roles);
            globalState.setUser(response.user || null);
            globalState.setRoles(response.roles || null);
            return response;
        }).catch((e: Error) => {
            globalState.setUser(null);
            globalState.setRoles(null);
            return {user: "", roles: ""} as LoginResponse;
        });
    }, [globalState])

    const fakeLoginAndUpdateGlobalState = useCallback(async (username: string) => {
        return SignupClient.developerFakeSigninAsOtherPerson(username).then((response) => {
            console.log("Login info: ", response.user, response.roles);
            globalState.setUser(response.user);
            globalState.setRoles(response.roles);
            return response;
        }).catch((e: Error) => {
            globalState.setUser(null);
            globalState.setRoles(null);
            return {user: "", roles: ""} as LoginResponse;
        });
    }, [globalState])

    const logoutAndUpdateGlobalState = useCallback(async () => {
        return logout().then(() => {
            globalState.setUser(null);
            globalState.setRoles(null);
        })
    }, [globalState])

    const signupAndReturnDisplayText = async (username: string, password: string) => {
        return signup(username, password).then((response) => {
            return [response, true]
        }).catch((e: any) => {
            if (e.response?.status === 409) {
              return [`Kontot ${username} existerar redan.`, false];
            } 
            return [`Registreringen kunde inte slutföras. \nVänta någon minut och försök igen eller kontakta Östhammar Friidrott för hjälp. \n\n ${e}`, false];
        })
    }

    const changePasswordAndReturnDisplayText = async (username: string, password: string) => {
        return changePassword(username, password).then((response) => {
            return [response, true];
        }).catch((e: Error) => {
            return [`Kunde inte slutföra ändringen av ditt lösenord. \nVänta någon minut och försök igen eller kontakta Östhammar Friidrott för hjälp. \n\n ${e}`, false];
        })
    }

    return {role, user: globalState.user, hasAtLeastRole, login: loginAndUpdateGlobalState, fakeLogin: fakeLoginAndUpdateGlobalState, logout: logoutAndUpdateGlobalState, signup: signupAndReturnDisplayText, getUserMode: () => role, hasUserMode: (role: Role) => hasAtLeastRole(role), changePassword: changePasswordAndReturnDisplayText};
}
